import { useState } from 'react';
import {
  Avatar,
  Badge,
  Box,
  CloseButton,
  Group,
  Image,
  Paper,
  Popover,
  Stack,
  Text,
  UnstyledButton,
} from '@mantine/core';
import {
  TbStarFilled,
  TbUser,
  TbPlus,
  TbCheck,
  TbDeviceMobile,
  TbInfoCircle,
} from 'react-icons/tb';
import type { LeaderboardEntry } from '@worker/types/worker-dashboard.type';

export interface LeaderboardWidgetProps {
  worker: LeaderboardEntry;
  leaderboard: LeaderboardEntry[];
  totalCount: number;
  podiumIconUrl?: string;
}

// TODO: Replace hardcoded colors (#f1a636, #9B9B9B, #1c8012)
// with design tokens from colors.scss on full React dashboard migration (Subtask 9).

const LEGEND_ITEMS = [
  {
    icon: <TbStarFilled size={21} color="#f1a636" />,
    label: 'Star Rating',
    description: 'Achieved when companies review you',
  },
  {
    icon: <TbUser size={21} color="#9B9B9B" />,
    label: 'Profile Status',
    description: 'Increase by completing profile',
  },
  {
    icon: <TbPlus size={21} color="#9B9B9B" />,
    label: 'Reviews Added',
    description: 'Rate any event gig worked',
  },
  {
    icon: <TbCheck size={21} color="#1c8012" />,
    label: 'Reviews Found Helpful',
    description: 'Times workers liked your reviews',
  },
  {
    icon: <TbDeviceMobile size={21} color="#9B9B9B" />,
    label: 'Trusted Herd App',
    description: 'Download for iOS or Android',
  },
];

function LegendContent({ onClose }: { onClose: () => void }) {
  return (
    <Stack gap={8} p={8} maw={240}>
      <Group justify="space-between" align="center" wrap="nowrap" gap={4}>
        <Stack gap={0}>
          <Text fw={700} fz={16} lh={1.2}>
            Legend
          </Text>
          <Text c="#9B9B9B" fz={13} lh={1.2}>
            Become a leader by ...
          </Text>
        </Stack>
        <CloseButton
          onClick={onClose}
          aria-label="Close"
          style={{ alignSelf: 'flex-start' }}
        />
      </Group>
      {LEGEND_ITEMS.map((item) => (
        <Group key={item.label} gap="sm" wrap="nowrap" align="center">
          <Box
            w={28}
            display="flex"
            style={{ flexShrink: 0, justifyContent: 'center' }}
          >
            {item.icon}
          </Box>
          <Stack gap={0}>
            <Text fw={700} fz={14}>
              {item.label}
            </Text>
            <Text c="#9B9B9B" fz={13}>
              {item.description}
            </Text>
          </Stack>
        </Group>
      ))}
    </Stack>
  );
}

function MetricBadges({ entry }: { entry: LeaderboardEntry }) {
  return (
    <Group gap={0} justify="space-between" wrap="nowrap">
      <Stack align="center" gap={2}>
        <TbStarFilled size={15} color="#f1a636" />
        <Text fz={13} c="#9B9B9B">
          {entry.rating != null ? entry.rating.toFixed(1) : 'N/A'}
        </Text>
      </Stack>
      <Stack align="center" gap={2}>
        <TbUser size={15} color="#9B9B9B" />
        <Text fz={13} c="#9B9B9B">
          {entry.profileCompletedPercentage}%
        </Text>
      </Stack>
      <Stack align="center" gap={2}>
        <TbPlus size={15} color="#9B9B9B" />
        <Text fz={13} c="#9B9B9B">
          {entry.reviewsAdded > 300 ? '300+' : entry.reviewsAdded}
        </Text>
      </Stack>
      <Stack align="center" gap={2}>
        <TbCheck size={15} color="#1c8012" />
        <Text fz={13} c="#9B9B9B">
          {entry.reviewsFoundHelpful}
        </Text>
      </Stack>
      <Stack align="center" gap={2}>
        <TbDeviceMobile size={15} color="#9B9B9B" />
        <Text fz={13} c="#9B9B9B">
          {entry.hasMobileApp ? 'yes' : 'no'}
        </Text>
      </Stack>
    </Group>
  );
}

function RankItem({
  entry,
  isCurrentWorker = false,
}: {
  entry: LeaderboardEntry;
  isCurrentWorker?: boolean;
}) {
  return (
    <Box
      style={{
        border: isCurrentWorker ? '1px solid #8c8c8c' : '1px solid #e3e3e3',
        borderRadius: 5,
      }}
      p="xs"
    >
      <Group gap="sm" wrap="nowrap">
        <Avatar src={entry.profileImageUrl} size={75} radius={4} />
        <Stack gap={4} flex={1}>
          <Group gap={6} wrap="nowrap">
            <Text fw={700} fz={15}>
              {isCurrentWorker ? `${entry.fullName} (you)` : entry.shortName}
            </Text>
            {entry.isPremium && (
              <Badge
                size="sm"
                color="#f2d032"
                c="#000"
                radius={2}
                tt="uppercase"
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  (window as any).$?.('#inNetworkVerifiedModal')?.modal('show');
                }}
              >
                In-Network
              </Badge>
            )}
          </Group>
          <MetricBadges entry={entry} />
        </Stack>
      </Group>
    </Box>
  );
}

export function LeaderboardWidget({
  worker,
  leaderboard,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  totalCount,
  podiumIconUrl,
}: LeaderboardWidgetProps) {
  const [legendOpened, setLegendOpened] = useState(false);

  return (
    <Paper px={10} py={20}>
      <Stack gap="sm">
        <Group gap="md" align="center">
          {podiumIconUrl && (
            <Stack align="center" gap={0}>
              <Image src={podiumIconUrl} alt="Leaders" w={30} />
              <Text component="span" fz={10} fw={700} c="#9B9B9B">
                Leaders
              </Text>
            </Stack>
          )}
          <Stack gap={0} flex={1}>
            <Text component="span" fw={700} fz={15} lh={1.3}>
              Local Market Leaders
            </Text>
            <Text component="span" fw={700} fz={15} lh={1.3} c="#9B9B9B">
              Based on Home Address
            </Text>
          </Stack>
          <Popover
            opened={legendOpened}
            onChange={setLegendOpened}
            position="bottom-end"
            withArrow
            shadow="md"
            withinPortal={false}
          >
            <Popover.Target>
              <UnstyledButton
                onClick={() => setLegendOpened((isOpened) => !isOpened)}
                aria-label="Show legend"
                style={{ alignSelf: 'flex-start' }}
              >
                <TbInfoCircle size={18} color="#666" />
              </UnstyledButton>
            </Popover.Target>
            <Popover.Dropdown>
              <LegendContent onClose={() => setLegendOpened(false)} />
            </Popover.Dropdown>
          </Popover>
        </Group>

        <RankItem entry={worker} isCurrentWorker />

        {leaderboard.map((entry) => (
          <RankItem key={entry.shortName} entry={entry} />
        ))}
      </Stack>
    </Paper>
  );
}
